import { reactive } from 'vue';

export interface DebugItem {
    value: any;
    context: string;
}

// Central reactive store for all dumped variables. Consumed by DebugOverlay.vue.
export const debugState = reactive<Record<string, DebugItem>>({});

/**
 * Reads the browser's error stack trace to figure out from which file and
 * line number the `dump()` function was called, so the overlay can show
 * a helpful label like "index.vue:25" instead of nothing.
 *
 * @returns A human-readable string like "index.vue:25", or a timestamp fallback.
 */
const getCallerContext = (): string => {
    try {
        const stackTrace = new Error().stack;

        if (!stackTrace) {
            return `Dump ${new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
        }

        // Split the stack into individual lines and find the first line that
        // does not point to this utility file itself.
        const stackLines = stackTrace.split('\n');
        let callerLine = '';

        for (let lineIndex = 1; lineIndex < stackLines.length; lineIndex++) {
            const currentLine = stackLines[lineIndex];
            if (!currentLine) { continue; }

            const isInternalLine = currentLine.includes('utils/debug.ts') || currentLine.includes('Error');
            if (!isInternalLine) {
                callerLine = currentLine;
                break;
            }
        }

        if (callerLine) {
            // Stack trace formats differ between browsers:
            //   Chrome:         "at setup (app/pages/index.vue:25:1)"
            //   Firefox/Safari: "setup@app/pages/index.vue:25:1"
            //
            // The regex captures the filename and the line number.
            const fileAndLinePattern = /([a-zA-Z0-9_.-]+\.[a-zA-Z0-9]+)(?:\?[^:]+)?:(\d+):\d+/;
            const match = callerLine.match(fileAndLinePattern);

            if (match && match[1] && match[2]) {
                const fileName = match[1].split('/').pop(); // e.g. "index.vue"
                const lineNumber = match[2];
                return `${fileName}:${lineNumber}`; // e.g. "index.vue:25"
            }
        }
    } catch {
        // Silently ignore any errors that occur during stack parsing.
    }

    // Fallback when we cannot determine the caller location.
    return `Dump ${new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
};

/**
 * Dumps a value to the debug overlay with an automatic label based on the
 * call location (e.g. "index.vue:25").
 *
 * Only active during development on the client side.
 *
 * @param value - The value to inspect.
 */
export function dump(value: any): void; // eslint-disable-line no-unused-vars

/**
 * Dumps a value to the debug overlay with a custom label.
 *
 * Only active during development on the client side.
 *
 * @param label - The label shown in the overlay sidebar.
 * @param value - The value to inspect.
 */
/* eslint-disable no-unused-vars */
export function dump(label: string, value: any): void;
/* eslint-enable no-unused-vars */
export function dump(labelOrValue: any, value?: any) {
    const callerContext = getCallerContext();
    let label: string,
        actualValue: any;

    if (arguments.length === 1) {
        // Called as dump(value) — use the caller location as the label.
        actualValue = labelOrValue;
        label = callerContext;
    } else {
        // Called as dump('myLabel', value) — use the provided label.
        label = String(labelOrValue);
        actualValue = value;
    }

    if ((process as any).dev && (process as any).client) {
        debugState[label] = { value: actualValue, context: callerContext };
    }
}
